import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Building2, DollarSign, RefreshCw } from 'lucide-react';
import { DASHBOARD_VIEWS } from '../../../utils/dashboardPreferences';
import {
  REPORTING_PERIOD_PRESETS,
  formatReportingPeriodLabel,
  normalizeReportingPeriod,
} from '../../../utils/reportingPeriod';
import DashboardViewSwitcher from './DashboardViewSwitcher';

const MODE_META = {
  executive: { icon: Building2, label: 'Executive Command Center', view: DASHBOARD_VIEWS.EXECUTIVE },
  operational: { icon: Activity, label: 'Operational Command Center', view: DASHBOARD_VIEWS.OPERATIONAL },
  finance: { icon: DollarSign, label: 'Finance View', view: DASHBOARD_VIEWS.FINANCE },
};

export default function DashboardCommandHeader({
  mode = 'executive',
  branding,
  period,
  onPeriodChange,
  onRefresh,
  isFetching = false,
  user,
  refreshedAt,
  statusMessage,
  showSettingsLink = true,
}) {
  const meta = MODE_META[mode] || MODE_META.executive;
  const EyebrowIcon = meta.icon;
  const eyebrowLabel = meta.label;
  const reportingPeriod = normalizeReportingPeriod(period);
  const isCustom = reportingPeriod.preset === REPORTING_PERIOD_PRESETS.CUSTOM;

  const updatePeriod = (patch) => {
    onPeriodChange?.(normalizeReportingPeriod({ ...reportingPeriod, ...patch }));
  };

  return (
    <div className="rounded-xl bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 text-white p-6 shadow-lg">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-blue-200 text-sm mb-1">
            <EyebrowIcon className="w-4 h-4" />
            <span>{eyebrowLabel}</span>
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold">{branding?.title}</h1>
          <p className="text-slate-300 text-sm mt-1">{branding?.subtitle}</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-300">
            Reporting period
            <select
              value={reportingPeriod.preset}
              onChange={(e) => updatePeriod({ preset: e.target.value })}
              className="mt-1 block rounded-md border-slate-600 bg-slate-800 text-white text-sm px-2 py-1.5 min-w-[10rem]"
            >
              <option value={REPORTING_PERIOD_PRESETS.LAST_30}>Last 30 days</option>
              <option value={REPORTING_PERIOD_PRESETS.LAST_90}>Last 90 days</option>
              <option value={REPORTING_PERIOD_PRESETS.LAST_180}>Last 180 days</option>
              <option value={REPORTING_PERIOD_PRESETS.CUSTOM}>Custom range</option>
            </select>
          </label>
          {isCustom && (
            <>
              <label className="text-xs text-slate-300">
                From
                <input
                  type="date"
                  value={reportingPeriod.startDate}
                  max={reportingPeriod.endDate}
                  onChange={(e) => updatePeriod({ startDate: e.target.value })}
                  className="mt-1 block rounded-md border-slate-600 bg-slate-800 text-white text-sm px-2 py-1.5"
                />
              </label>
              <label className="text-xs text-slate-300">
                To
                <input
                  type="date"
                  value={reportingPeriod.endDate}
                  min={reportingPeriod.startDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => updatePeriod({ endDate: e.target.value })}
                  className="mt-1 block rounded-md border-slate-600 bg-slate-800 text-white text-sm px-2 py-1.5"
                />
              </label>
            </>
          )}
          <button
            type="button"
            onClick={onRefresh}
            disabled={isFetching}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-600 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-700/80">
        <DashboardViewSwitcher
          user={user}
          currentView={meta.view}
          compact
          variant="dark"
        />
      </div>

      {(refreshedAt || statusMessage) && (
        <p className="text-[11px] text-slate-400 mt-3">
          {refreshedAt && `Data refreshed: ${new Date(refreshedAt).toLocaleString()}`}
          {statusMessage && `${refreshedAt ? ' · ' : ''}${statusMessage}`}
          {` · Period: ${formatReportingPeriodLabel(reportingPeriod)}`}
          {showSettingsLink && mode === 'executive' && (
            <>
              {' · '}
              <Link to="/settings?tab=dashboard" className="text-blue-300 hover:underline">
                Add or edit tiles
              </Link>
              {' · '}
              <Link to="/reporting?service=execReport&report=compare-reports" className="text-blue-300 hover:underline">
                Compare & share reports
              </Link>
            </>
          )}
          {showSettingsLink && mode !== 'executive' && (
            <>
              {' · '}
              <Link to="/settings?tab=dashboard" className="text-blue-300 hover:underline">
                Dashboard settings
              </Link>
              {' · '}
              <Link to="/reporting" className="text-blue-300 hover:underline">
                Reports & sharing
              </Link>
            </>
          )}
        </p>
      )}
    </div>
  );
}
